const STORE_API = "/wp-json/wc/store/v1/cart";

let cartNonce = window.wcBlocksMiddlewareConfig?.storeApiNonce || "";
let currentCart = null;
let toastTimer = 0;

const byId = (id) => document.getElementById(id);
const escapeHtml = (value) => String(value ?? "").replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;").replaceAll("'", "&#039;");

function showToast(message) {
  const toast = byId("toast");
  const messageNode = byId("toastMsg");
  if (!toast || !messageNode) return;
  messageNode.textContent = message;
  toast.classList.add("is-visible");
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => toast.classList.remove("is-visible"), 2600);
}

function shopUrl(search = "") {
  const url = new URL("/shop/", window.location.origin);
  if (search) url.searchParams.set("search", search);
  return url.toString();
}

function navigateToShop(search) {
  window.location.assign(shopUrl(search));
}

function setMobileMenu(open) {
  const toggle = byId("mobileMenuBtn");
  const menu = byId("mobileMenu");
  if (!toggle || !menu) return;
  menu.hidden = !open;
  menu.classList.toggle("is-open", open);
  toggle.classList.toggle("is-open", open);
  toggle.setAttribute("aria-expanded", String(open));
  toggle.setAttribute("aria-label", open ? "Close navigation menu" : "Open navigation menu");
}

function setMobileSearch(open) {
  const toggle = byId("mobileSearchBtn");
  const wrap = byId("mobileSearchWrap");
  if (!toggle || !wrap) return;
  wrap.hidden = !open;
  toggle.setAttribute("aria-expanded", String(open));
  if (open) byId("searchInputMobile")?.focus();
}

function markCurrentNavigation() {
  const currentPath = window.location.pathname.replace(/\/+$/, "") || "/";
  document.querySelectorAll(".pfx-header a[href], .pfx-footer a[href]").forEach((link) => {
    const targetPath = new URL(link.href, window.location.origin).pathname.replace(/\/+$/, "") || "/";
    link.toggleAttribute("aria-current", currentPath === targetPath && !link.hash);
  });
}

function initNavigation() {
  byId("mobileMenuBtn")?.addEventListener("click", () => setMobileMenu(Boolean(byId("mobileMenu")?.hidden)));
  byId("mobileSearchBtn")?.addEventListener("click", () => setMobileSearch(Boolean(byId("mobileSearchWrap")?.hidden)));
  document.querySelectorAll("[data-pfx-close-menu]").forEach((link) => link.addEventListener("click", () => setMobileMenu(false)));
  document.querySelectorAll("[data-pfx-search-form]").forEach((form) => form.addEventListener("submit", (event) => {
    event.preventDefault();
    const search = form.querySelector("input[type='search']")?.value.trim() ?? "";
    if (search) navigateToShop(search);
  }));
  window.addEventListener("resize", () => { if (window.matchMedia("(min-width: 1024px)").matches) setMobileMenu(false); });
  window.addEventListener("popstate", markCurrentNavigation);
  markCurrentNavigation();
}

function cartSettings() {
  const root = document.querySelector("[data-pfx-delivery-threshold]");
  const number = (value, fallback) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
  };
  return {
    threshold: number(root?.dataset.pfxDeliveryThreshold, 60),
    fee: number(root?.dataset.pfxDeliveryFee, 10),
    emptyMessage: root?.dataset.pfxEmptyMessage || "Your cart is empty.",
  };
}

async function cartRequest(path = "", options = {}, canRetry = true) {
  const headers = new Headers(options.headers || {});
  headers.set("Accept", "application/json");
  if (options.body) headers.set("Content-Type", "application/json");
  if (cartNonce) headers.set("Nonce", cartNonce);
  const response = await fetch(`${STORE_API}${path}`, { credentials: "same-origin", ...options, headers });
  const nextNonce = response.headers.get("Nonce");
  if (nextNonce) cartNonce = nextNonce;
  if ((response.status === 401 || response.status === 403) && canRetry) {
    // Nonce was missing/stale (e.g. header stripped by a proxy): refresh it and retry once.
    cartNonce = window.wcBlocksMiddlewareConfig?.storeApiNonce || cartNonce;
    try {
      const fresh = await fetch(STORE_API, { credentials: "same-origin", headers: { Accept: "application/json" } });
      const refreshed = fresh.headers.get("Nonce");
      if (refreshed) cartNonce = refreshed;
    } catch (nonceError) { /* keep whatever nonce we have */ }
    return cartRequest(path, options, false);
  }
  if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
  return response.status === 204 ? null : response.json();
}

function cartCurrency(cart) {
  const totals = cart?.totals || {};
  return { decimals: Number(totals.currency_minor_unit ?? 2), prefix: totals.currency_prefix ?? "$", suffix: totals.currency_suffix ?? "" };
}

function formatMoney(minorAmount, currency) {
  return `${currency.prefix}${(Number(minorAmount) / (10 ** currency.decimals)).toFixed(currency.decimals)}${currency.suffix}`;
}

function itemPrice(item) {
  return Number(item?.prices?.price ?? 0);
}

function renderCart(cart) {
  currentCart = cart || { items: [] };
  const items = Array.isArray(currentCart.items) ? currentCart.items : [];
  const currency = cartCurrency(currentCart);
  const settings = cartSettings();
  const itemCount = items.reduce((total, item) => total + Number(item.quantity || 0), 0);
  const subtotal = items.reduce((total, item) => total + (itemPrice(item) * Number(item.quantity || 0)), 0);
  const threshold = settings.threshold * (10 ** currency.decimals);
  const delivery = subtotal > 0 && subtotal < threshold ? settings.fee * (10 ** currency.decimals) : 0;
  if (byId("cartCount")) byId("cartCount").textContent = String(itemCount);
  if (byId("cartSubtotal")) byId("cartSubtotal").textContent = formatMoney(subtotal, currency);
  if (byId("cartDeliveryFee")) byId("cartDeliveryFee").textContent = delivery ? formatMoney(delivery, currency) : "FREE";
  if (byId("cartTotal")) byId("cartTotal").textContent = formatMoney(subtotal + delivery, currency);
  if (byId("deliveryFeeNote")) byId("deliveryFeeNote").textContent = !items.length ? "" : delivery ? `Add ${formatMoney(threshold - subtotal, currency)} more to unlock free delivery` : "You've unlocked free delivery!";
  const itemList = byId("cartItems");
  if (!itemList) return;
  if (!items.length) {
    itemList.innerHTML = `<p class="mini-cart__empty">${escapeHtml(settings.emptyMessage)}</p>`;
    return;
  }
  itemList.innerHTML = items.map((item) => {
    const image = item.images?.[0]?.thumbnail || item.images?.[0]?.src || "";
    const lineTotal = itemPrice(item) * Number(item.quantity || 0);
    const imageMarkup = image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(item.name)}">` : '<div aria-hidden="true"></div>';
    return `<article class="mini-cart__item">${imageMarkup}<div><strong class="mini-cart__item-name">${escapeHtml(item.name)}</strong><span class="mini-cart__item-price">${formatMoney(itemPrice(item), currency)} each</span><div class="mini-cart__quantity"><button type="button" data-pfx-cart-quantity="-1" data-pfx-cart-key="${escapeHtml(item.key)}" aria-label="Decrease ${escapeHtml(item.name)} quantity">−</button><span>${Number(item.quantity || 0)}</span><button type="button" data-pfx-cart-quantity="1" data-pfx-cart-key="${escapeHtml(item.key)}" aria-label="Increase ${escapeHtml(item.name)} quantity">+</button></div></div><div><strong class="mini-cart__line-total">${formatMoney(lineTotal, currency)}</strong><button class="mini-cart__remove" type="button" data-pfx-cart-remove data-pfx-cart-key="${escapeHtml(item.key)}" aria-label="Remove ${escapeHtml(item.name)}">Remove</button></div></article>`;
  }).join("");
}

async function refreshCart(showError = false) {
  try { renderCart(await cartRequest()); } catch (error) { if (showError) showToast("We couldn't load the cart. Please try again."); }
}

function setCartOpen(open) {
  const drawer = byId("cartDrawer");
  const overlay = byId("cartOverlay");
  if (!drawer || !overlay) return;
  drawer.classList.toggle("is-open", open);
  overlay.classList.toggle("is-open", open);
  drawer.setAttribute("aria-hidden", String(!open));
  overlay.setAttribute("aria-hidden", String(!open));
  document.body.classList.toggle("pfx-cart-open", open);
  document.querySelectorAll("[data-pfx-cart-open]").forEach((button) => button.setAttribute("aria-expanded", String(open)));
  if (open) { refreshCart(); window.setTimeout(() => drawer.focus(), 0); }
}

async function addCartItem(productId, productName = "Product") {
  if (!Number.isInteger(productId) || productId <= 0) return;
  try {
    renderCart(await cartRequest("/add-item", { method: "POST", body: JSON.stringify({ id: productId, quantity: 1 }) }));
    showToast(`${productName} added to cart`);
  } catch (error) { showToast("We couldn't add this item to the cart. Please try again."); }
}

async function updateCartItem(key, quantity) {
  if (!key) return;
  try { renderCart(await cartRequest("/update-item", { method: "POST", body: JSON.stringify({ key, quantity }) })); } catch (error) { showToast("We couldn't update the cart. Please try again."); }
}

async function removeCartItem(key) {
  if (!key) return;
  try { renderCart(await cartRequest("/remove-item", { method: "POST", body: JSON.stringify({ key }) })); } catch (error) { showToast("We couldn't update the cart. Please try again."); }
}

function checkoutCart() {
  const items = Array.isArray(currentCart?.items) ? currentCart.items : [];
  if (!items.length) { showToast("Your cart is empty"); return; }
  window.location.assign("/checkout/");
}

function initCart() {
  byId("cartOverlay")?.addEventListener("click", () => setCartOpen(false));
  byId("cartDrawer")?.addEventListener("keydown", (event) => { if (event.key === "Escape") setCartOpen(false); });
  document.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest("[data-pfx-cart-open]")) { setCartOpen(true); return; }
    if (target.closest("[data-pfx-cart-close]")) { setCartOpen(false); return; }
    const addButton = target.closest("[data-pfx-add-to-cart]");
    if (addButton) { event.preventDefault(); addCartItem(Number(addButton.getAttribute("data-product-id")), addButton.getAttribute("data-pfx-product-name") || "Product"); return; }
    const quantityButton = target.closest("[data-pfx-cart-quantity]");
    if (quantityButton) {
      const key = quantityButton.getAttribute("data-pfx-cart-key");
      const currentItem = currentCart?.items?.find((item) => item.key === key);
      const quantity = Number(currentItem?.quantity || 0) + Number(quantityButton.getAttribute("data-pfx-cart-quantity"));
      if (quantity <= 0) removeCartItem(key); else updateCartItem(key, quantity);
      return;
    }
    const removeButton = target.closest("[data-pfx-cart-remove]");
    if (removeButton) removeCartItem(removeButton.getAttribute("data-pfx-cart-key"));
  });
  document.addEventListener("pfx:add-to-cart", (event) => addCartItem(Number(event.detail?.productId), event.detail?.productName || "Product"));
  document.addEventListener("pfx:toast", (event) => showToast(event.detail?.message || "Done"));
  document.addEventListener("wc-blocks_added_to_cart", () => { window.setTimeout(() => refreshCart(), 0); });
  document.querySelector("[data-pfx-cart-checkout]")?.addEventListener("click", checkoutCart);
  document.addEventListener("keydown", (event) => { if (event.key === "Escape") setCartOpen(false); });
  refreshCart();
}

function init() {
  initNavigation();
  initCart();
}

if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init, { once: true });
else init();
